import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, BellRing, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '../ui/utils';
import {
  ORGAN_LABEL,
  ORGAN_ORDER,
  bandStyle,
  isInsufficient,
  unscorableGap,
  type OrganBand,
  type OrganName,
  type OrganScore,
} from '../response/organHealth';
import { RiskMeter } from '../response/RiskTrack';
import { fetchOrganScores, fetchOrganAlerts, type OrganAlert } from '../../api/organHealthApi';

/**
 * Compact, drawer-friendly organ-health summary for the clinician patient panel.
 * Replaces the retired per-condition-engine twin view: shows the worst-organ
 * Overall band plus a row per organ in ORGAN_ORDER (all ten), and a count
 * of active organ alerts. Read-only — twins build/refresh automatically after a
 * patient launch; recompute lives in the admin console.
 */
const clampScore = (n: number) => Math.max(0, Math.min(100, n));

// Per-organ IDENTITY tint from the categorical chart palette (NOT the severity
// band palette): a small dot in the name zone gives each organ a stable colour
// for fast recognition, while severity stays entirely on the meter + band label
// so the two colour systems never compete. Static class strings (never
// `bg-chart-${n}`) so Tailwind keeps them.
// The five v0.12 endocrine organs were added to the twin without a tint, so half
// the list rendered a grey dot while the other half had identity colour. The
// categorical palette has five hues and there are ten organs, so hues repeat --
// acceptable because the dot is a recognition AID beside a always-present label,
// never the identifier. Repeats are spaced so two same-coloured dots are never
// adjacent in ORGAN_ORDER.
const ORGAN_TINT: Record<string, string> = {
  kidney: 'bg-chart-1',
  liver: 'bg-chart-2',
  lung: 'bg-chart-3',
  heart: 'bg-chart-4',
  brain: 'bg-chart-5',
  thyroid: 'bg-chart-1',
  adrenal: 'bg-chart-2',
  pancreas: 'bg-chart-3',
  pituitary: 'bg-chart-4',
  gonad: 'bg-chart-5',
};

/** One organ row: label + a band-colored score meter. A scored organ shows a
 * filled bar (severity is band-colour + icon + label, never colour alone).
 *
 * An UNSCORED organ used to end at a muted "Awaiting data" over an empty track,
 * which was true and nearly useless — it threw away two things the scorer had
 * already computed for exactly this state:
 *
 *  - the MODIFIABLE-RISK track, which is scored independently of function. A
 *    heart whose LVEF was never measured can still carry a scored moderate
 *    risk, and "we cannot stage this heart but her modifiable risk is moderate"
 *    is the actionable half of the picture. Rendered with the shared RiskMeter
 *    so it keeps its own visual language (neutral fill, fills toward GOOD, band
 *    word always present) and can never be misread as a function score.
 *  - the NAMED GAP — the one test that would unlock the score. A gap a
 *    clinician can close beats a status they can only wait on.
 *
 * Ten cards reading "Awaiting data" on a patient the system knows plenty about
 * is what made a working pipeline look broken. */
function OrganRow({ organ }: { organ: OrganScore }) {
  const insufficient = isInsufficient(organ);
  const b = bandStyle(organ.band as OrganBand);
  const Icon = b.icon;
  const label = ORGAN_LABEL[organ.organ as OrganName] ?? organ.organ;
  const riskScored = typeof organ.risk_score === 'number';
  const gap = insufficient ? unscorableGap(organ) : null;

  return (
    <li className="rounded-lg border border-border-subtle bg-surface-sunken/40 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('size-2 shrink-0 rounded-full', ORGAN_TINT[organ.organ] ?? 'bg-text-tertiary')} aria-hidden />
          <span className="truncate text-[12px] font-medium text-foreground">{label}</span>
        </span>
        {insufficient ? (
          // Names the FUNCTION track explicitly: with a risk read directly
          // below, an unqualified "Not scored" would read as "nothing here".
          <span className="shrink-0 text-[11px] text-text-tertiary">Function not scored</span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] leading-none">
            <Icon className={cn('size-3.5', b.accent)} aria-hidden />
            <span className={cn('font-medium', b.accent)}>{b.label}</span>
            <span className="tabular-nums font-medium text-foreground">
              {organ.score}
              <span className="text-text-tertiary">/100</span>
            </span>
          </span>
        )}
      </div>

      {insufficient ? (
        <>
          {gap ? (
            <p className="mt-1 text-[11px] leading-snug text-text-tertiary">{gap}</p>
          ) : null}
          {riskScored ? (
            <RiskMeter score={organ.risk_score} band={organ.risk_band} className="mt-1.5" />
          ) : null}
        </>
      ) : (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
          {organ.score == null ? null : (
            <div className={cn('h-full rounded-full transition-[width] duration-300', b.fill)}
              style={{ width: `${clampScore(organ.score)}%` }} />
          )}
        </div>
      )}
    </li>
  );
}

export function ClinicianTwinSummary({ patientId }: { patientId: string }) {
  const [scores, setScores] = useState<OrganScore[]>([]);
  const [alerts, setAlerts] = useState<OrganAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const pid = patientId.trim();
    if (!pid) return;
    setLoading(true);
    setError(null);
    try {
      const [s, a] = await Promise.all([fetchOrganScores(pid), fetchOrganAlerts(pid, 20)]);
      setScores(s);
      // "Active" = not yet acknowledged; an acked alert has been seen and
      // handled, so the panel's attention chip must not keep counting it.
      setAlerts(a.filter((alert) => !alert.acknowledged_at));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load organ health');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-border-subtle bg-surface-sunken/40 px-3 py-6 text-[11px] text-text-tertiary">
        <Loader2 className="size-3.5 animate-spin" aria-hidden /> Loading organ health…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive-subtle px-3 py-3 text-[11px] text-destructive-text">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>{error}</span>
      </div>
    );
  }
  if (scores.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border-subtle bg-surface-sunken/40 px-3 py-5 text-center">
        <p className="text-[11px] leading-relaxed text-text-tertiary">
          This patient's organ health is being prepared automatically. Check back in a few
          minutes — no action needed.
        </p>
      </div>
    );
  }

  return <OrganTwinPanel scores={scores} alerts={alerts} onRefresh={() => void load()} />;
}

/** Pure presentation for the loaded state: the Overall card + one metered row per
 * organ. Split from the fetching wrapper so it is trivially testable/previewable
 * with fixture data. */
export function OrganTwinPanel({
  scores,
  alerts,
  onRefresh,
}: {
  scores: OrganScore[];
  alerts: OrganAlert[];
  onRefresh: () => void;
}) {
  const overall = scores.find((s) => s.organ === 'overall') ?? null;
  const organs = ORGAN_ORDER
    .map((name) => scores.find((s) => s.organ === name))
    .filter((s): s is OrganScore => Boolean(s));
  const overallBand = bandStyle((overall?.band ?? 'unknown') as OrganBand);
  const OverallIcon = overallBand.icon;
  const scored = overall != null && overall.score != null;
  // Organ rows exist, so scoring RAN and simply had nothing gradable to work
  // with. Telling that clinician the twin "builds automatically as clinical
  // data arrives" promises a result that is never coming: the build already
  // finished. The distinction is the difference between "wait" and "order the
  // test", so the panel must not blur it.
  const scoringRan = organs.length > 0;
  // The risk track scores independently of function, and frequently scores when
  // function cannot: risk reads lifestyle, vitals and social factors, while
  // function needs an anchoring lab. Saying only "not scored" hides a number the
  // clinician can act on today.
  const riskScoredCount = organs.filter(
    (o) => o.organ !== 'overall' && typeof o.risk_score === 'number',
  ).length;
  // How many organs actually have a FUNCTION score — not how many exist.
  // The overall line used `organs.length` and so announced "across 10 scored
  // organs" for a patient where nine rows underneath it read "Function not
  // scored" and only the kidney carried a number. The overall was anchored to
  // exactly one organ; claiming ten made a single-lab reading look like a
  // ten-organ consensus, which is the opposite of what the clinician needs to
  // know about how much the 78 is worth.
  const functionScoredCount = organs.filter(
    (o) => o.organ !== 'overall' && typeof o.score === 'number',
  ).length;

  return (
    <div className="space-y-3">
      <div className={cn('rounded-lg border bg-surface-sunken/40 px-3 py-2.5', scored ? overallBand.surface : 'border-border-subtle')}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[9px] font-medium uppercase tracking-[0.14em] text-text-tertiary">Overall</div>
            {scored ? (
              <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-2xl font-semibold leading-none tabular-nums text-foreground">{overall?.score}</span>
                <span className="text-[10px] text-text-tertiary">/ 100</span>
                <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', overallBand.accent)}>
                  <OverallIcon className="size-3.5" aria-hidden />
                  {overallBand.label}
                </span>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-1.5">
                <Activity className="size-4 shrink-0 text-text-tertiary" aria-hidden />
                {/* Names the TRACK. A bare "Not scored" sat directly above four
                    organs each showing "Moderate risk · 60" -- every statement
                    true, and together they read as broken. The overall roll-up
                    is a FUNCTION roll-up; the risk track is scored separately
                    and often scores when function cannot. */}
                <span className="text-sm font-medium text-foreground">
                  {scoringRan ? 'Function not scored' : 'Not scored yet'}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onRefresh}
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-primary transition-colors hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RefreshCw className="size-3" aria-hidden />
            Refresh
          </button>
        </div>
        {scored ? (
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
            <div className={cn('h-full rounded-full transition-[width] duration-300', overallBand.fill)}
              style={{ width: `${clampScore(overall?.score ?? 0)}%` }} />
          </div>
        ) : null}
        <p className="mt-2 flex flex-wrap items-center gap-x-1 text-[10px] leading-4 text-text-tertiary">
          {scored
            ? `Worst-organ-anchored across ${functionScoredCount} scored organ${functionScoredCount === 1 ? '' : 's'}${
                functionScoredCount < organs.length ? ` of ${organs.length}` : ''
              }.`
            : scoringRan
              ? `${riskScoredCount > 0
                    ? `Modifiable risk scored for ${riskScoredCount} of ${organs.length} organs. Organ FUNCTION could not be graded from the values on file`
                    : `Scored ${organs.length} organ${organs.length === 1 ? '' : 's'}; none could be graded from the values on file`} — each row names the test that would unlock it.`
              : 'Builds automatically as clinical data arrives — no action needed.'}
          {alerts.length > 0 ? (
            <span className="inline-flex items-center gap-0.5 font-medium text-status-negative-text">
              <BellRing className="size-3" aria-hidden /> {alerts.length} active alert{alerts.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </p>
      </div>

      {organs.length > 0 ? (
        <ul className="space-y-1.5">
          {organs.map((organ) => (
            <OrganRow key={organ.organ} organ={organ} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
